import type { ComputedRef, Ref } from "vue";
import { darkenColor } from "@donoor/utils/color";
import type { SSRPaymentData } from "./usePaySlugSeo";

interface UsePaySlugDisplayOptions {
  isPreviewMode: ComputedRef<boolean>;
  ssrPaymentData: Ref<SSRPaymentData | null>;
  ssrPaymentPage: NonNullable<SSRPaymentData["data"]>["paymentPage"] | undefined;
  previewGoal: Ref<number | null | undefined>;
  previewRaised: Ref<number | undefined>;
  previewEndDate: Ref<string | null | undefined>;
  previewShowGoalProgress: Ref<boolean | undefined>;
  previewAmounts: Ref<number[] | null | undefined> | ComputedRef<number[] | null | undefined>;
  previewDefaultAmountIndex: Ref<number>;
  paymentForm: {
    suggestedAmounts: Ref<number[]>;
    form: Ref<{ amount: string }>;
    subscriptionPeriod: Ref<string>;
  };
  donationData: {
    donoorFeeRate: Ref<number>;
  };
  finalAmountWithDiscount: ComputedRef<number>;
  formatAmount: (amount: number) => string;
  youtubeUrl: Ref<string | null | undefined>;
}

/**
 * Regroupe les computeds d'affichage pour /pay/[slug] :
 *   - Objectif de collecte (goal/raised/endDate/showGoalProgress/daysRemaining)
 *   - Montants suggérés (preview vs SSR vs default)
 *   - Texte "frais couverts" et suffixe d'abonnement
 *   - Extraction du YouTube video ID
 *   - Affichage formaté du montant total
 *   - Helper darkenColor (CSS hover)
 *
 * Synchronise `paymentForm.suggestedAmounts` et `paymentForm.form.amount`
 * avec les montants suggérés (preview ou SSR), via watch immédiat.
 */
export function usePaySlugDisplay(opts: UsePaySlugDisplayOptions) {
  const { t } = useI18n();
  const {
    isPreviewMode,
    ssrPaymentData,
    ssrPaymentPage,
    previewGoal,
    previewRaised,
    previewEndDate,
    previewShowGoalProgress,
    previewAmounts,
    previewDefaultAmountIndex,
    paymentForm,
    donationData,
    finalAmountWithDiscount,
    formatAmount,
    youtubeUrl,
  } = opts;

  // Utilitaire d'assombrissement d'une couleur hex (CSS hover) — importé
  // depuis @donoor/utils/color (source unique cross-app).

  /** Renvoie la valeur preview si présente en mode preview, sinon la valeur API. */
  function previewOr<T>(previewVal: T | undefined, apiVal: T): T {
    return isPreviewMode.value && previewVal !== undefined
      ? previewVal
      : apiVal;
  }

  const goalAmount = computed(() =>
    previewOr(previewGoal.value, ssrPaymentData.value?.data?.goal || null),
  );
  const raisedAmount = computed(() =>
    previewOr(previewRaised.value, ssrPaymentData.value?.data?.raised || 0),
  );
  const goalEndDate = computed(() =>
    previewOr(
      previewEndDate.value,
      ssrPaymentData.value?.data?.paymentPage?.goalEndDate || null,
    ),
  );
  const showGoalProgress = computed(() => {
    if (isPreviewMode.value && previewShowGoalProgress.value !== undefined) {
      return previewShowGoalProgress.value;
    }
    // 2 structures de réponse possibles : payment-link (paymentPage) ou
    // fallback QR code (data direct). On accepte uniquement `true` explicite.
    return (
      ssrPaymentData.value?.data?.paymentPage?.showGoalProgress === true ||
      ssrPaymentData.value?.data?.showGoalProgress === true
    );
  });

  // Jours restants jusqu'à la date limite (null si pas de date).
  const daysRemaining = computed(() => {
    if (!goalEndDate.value) return null;
    const end = new Date(goalEndDate.value);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    if (diff <= 0) return 0;
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  });

  // ─────────────────────────────────────────────────────────────────────────
  // Montants suggérés
  // ─────────────────────────────────────────────────────────────────────────
  const customSuggestedAmounts = computed(() => {
    if (
      isPreviewMode.value &&
      previewAmounts.value &&
      previewAmounts.value.length > 0
    ) {
      return previewAmounts.value;
    }
    const amounts = ssrPaymentPage?.amounts;
    if (amounts && Array.isArray(amounts) && amounts.length > 0) {
      return amounts;
    }
    return [10, 20, 50];
  });
  const defaultAmountIdx = computed(() => {
    if (isPreviewMode.value) return previewDefaultAmountIndex.value;
    return ssrPaymentPage?.defaultAmountIndex ?? 0;
  });

  watch(
    [customSuggestedAmounts, defaultAmountIdx],
    ([amounts, defaultIdx]) => {
      if (!amounts || amounts.length === 0) return;
      paymentForm.suggestedAmounts.value = amounts;
      const safeIdx = Math.min(defaultIdx, amounts.length - 1);
      const defaultAmount = amounts[safeIdx] || amounts[0];
      if (
        !paymentForm.form.value.amount ||
        paymentForm.form.value.amount === "20" ||
        paymentForm.form.value.amount === "10"
      ) {
        paymentForm.form.value.amount = String(defaultAmount);
      }
    },
    { immediate: true },
  );

  // ─────────────────────────────────────────────────────────────────────────
  // YouTube — extraction du video ID (template iframe désactivé pour l'instant)
  // ─────────────────────────────────────────────────────────────────────────
  const youtubeVideoId = computed(() => {
    const url = youtubeUrl.value;
    if (!url) return null;
    const match = url.match(
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([a-zA-Z0-9_-]{11})/,
    );
    return match ? match[1] : null;
  });

  // Texte conditionnel pour le label "frais couverts".
  const feesText = computed(() =>
    donationData.donoorFeeRate.value > 0 ? t("don.paySlugDisplay.fraisStripeEtDonoor") : "Stripe",
  );

  // Suffixe d'abonnement ("/semaine", "/mois", "/an") — utilisé 4× dans le
  // template (desktop/mobile × banner/no-banner), factorisé ici.
  const subscriptionSuffix = computed(() => {
    switch (paymentForm.subscriptionPeriod.value) {
      case "weekly":
        return t("don.periodes.semaine");
      case "monthly":
        return t("don.periodes.mois");
      default:
        return t("don.periodes.an");
    }
  });

  // Affichage formaté du montant total (réutilisé desktop & mobile).
  const displayAmount = computed(() =>
    finalAmountWithDiscount.value > 0
      ? formatAmount(finalAmountWithDiscount.value)
      : "—",
  );

  return {
    darkenColor,
    goalAmount,
    raisedAmount,
    goalEndDate,
    showGoalProgress,
    daysRemaining,
    youtubeVideoId,
    feesText,
    subscriptionSuffix,
    displayAmount,
  };
}
